import { useCallback, useMemo } from 'react';
import useSearchParams from '../../core/hooks/useSearchParams';
import type { ExpenseFilters } from './useExpenses';

export type DatePreset =
    | 'this-month'
    | 'last-month'
    | 'this-quarter'
    | 'last-quarter'
    | 'this-year'
    | 'last-year'
    | 'all-time';

const parseOptionalInt = (value: string | undefined): number | undefined => {
    if (!value) return undefined;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? undefined : parsed;
};

export const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month, 0).getDate();
};

const getQuarterRange = (quarter: number): { startMonth: number; endMonth: number } => {
    const startMonth = (quarter - 1) * 3 + 1;
    return { startMonth, endMonth: startMonth + 2 };
};

const getCurrentQuarter = (month: number): number => {
    return Math.ceil(month / 3);
};

const useExpenseFilters = () => {
    const { params, updateParam } = useSearchParams();

    const currentYear = new Date().getFullYear();

    const filters: ExpenseFilters = useMemo(() => {
        const fromYear = parseOptionalInt(params.fromYear) ?? currentYear;
        const fromMonth = parseOptionalInt(params.fromMonth);
        const fromDay = parseOptionalInt(params.fromDay);
        const toYear = parseOptionalInt(params.toYear);
        const toMonth = parseOptionalInt(params.toMonth);
        const toDay = parseOptionalInt(params.toDay);
        const categoryId = parseOptionalInt(params.categoryId);
        const type = (params.type as ExpenseFilters['type']) || 'all';

        let startDate: string | undefined;
        let endDate: string | undefined;

        if (fromYear) {
            const month = fromMonth ?? 1;
            const day = fromDay ?? 1;
            startDate = `${fromYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }

        if (toYear) {
            const month = toMonth ?? 12;
            const maxDay = getDaysInMonth(toYear, month);
            const day = toDay ?? maxDay;
            endDate = `${toYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        } else if (fromYear && !toYear) {
            const month = toMonth ?? 12;
            const maxDay = getDaysInMonth(fromYear, month);
            const day = toDay ?? maxDay;
            endDate = `${fromYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }

        return {
            fromYear,
            fromMonth,
            fromDay,
            toYear,
            toMonth,
            toDay,
            categoryId,
            type,
            startDate,
            endDate,
        };
    }, [params, currentYear]);

    const updateFilters = useCallback(
        (updates: Partial<ExpenseFilters>) => {
            // Check if key exists in updates (not just if value is defined)
            // This allows explicitly setting values to undefined to clear them
            if ('fromYear' in updates) {
                updateParam('fromYear', updates.fromYear?.toString() ?? '');
            }
            if ('fromMonth' in updates) {
                updateParam('fromMonth', updates.fromMonth?.toString() ?? '');
            }
            if ('fromDay' in updates) {
                updateParam('fromDay', updates.fromDay?.toString() ?? '');
            }
            if ('toYear' in updates) {
                updateParam('toYear', updates.toYear?.toString() ?? '');
            }
            if ('toMonth' in updates) {
                updateParam('toMonth', updates.toMonth?.toString() ?? '');
            }
            if ('toDay' in updates) {
                updateParam('toDay', updates.toDay?.toString() ?? '');
            }
            if ('categoryId' in updates) {
                updateParam('categoryId', updates.categoryId?.toString() ?? '');
            }
            if ('type' in updates) {
                updateParam('type', updates.type === 'all' ? '' : (updates.type ?? ''));
            }
        },
        [updateParam]
    );

    const applyPreset = useCallback(
        (preset: DatePreset) => {
            const now = new Date();
            const year = now.getFullYear();
            const month = now.getMonth() + 1;
            const quarter = getCurrentQuarter(month);

            switch (preset) {
                case 'this-month':
                    updateParam('fromYear', year.toString());
                    updateParam('fromMonth', month.toString());
                    updateParam('fromDay', '');
                    updateParam('toYear', '');
                    updateParam('toMonth', month.toString());
                    updateParam('toDay', '');
                    break;

                case 'last-month': {
                    const lastMonth = month === 1 ? 12 : month - 1;
                    const lastMonthYear = month === 1 ? year - 1 : year;
                    updateParam('fromYear', lastMonthYear.toString());
                    updateParam('fromMonth', lastMonth.toString());
                    updateParam('fromDay', '');
                    updateParam('toYear', '');
                    updateParam('toMonth', lastMonth.toString());
                    updateParam('toDay', '');
                    break;
                }

                case 'this-quarter': {
                    const { startMonth, endMonth } = getQuarterRange(quarter);
                    updateParam('fromYear', year.toString());
                    updateParam('fromMonth', startMonth.toString());
                    updateParam('fromDay', '');
                    updateParam('toYear', '');
                    updateParam('toMonth', endMonth.toString());
                    updateParam('toDay', '');
                    break;
                }

                case 'last-quarter': {
                    const lastQuarter = quarter === 1 ? 4 : quarter - 1;
                    const lastQuarterYear = quarter === 1 ? year - 1 : year;
                    const { startMonth, endMonth } = getQuarterRange(lastQuarter);
                    updateParam('fromYear', lastQuarterYear.toString());
                    updateParam('fromMonth', startMonth.toString());
                    updateParam('fromDay', '');
                    updateParam('toYear', '');
                    updateParam('toMonth', endMonth.toString());
                    updateParam('toDay', '');
                    break;
                }

                case 'this-year':
                    updateParam('fromYear', year.toString());
                    updateParam('fromMonth', '');
                    updateParam('fromDay', '');
                    updateParam('toYear', '');
                    updateParam('toMonth', '');
                    updateParam('toDay', '');
                    break;

                case 'last-year':
                    updateParam('fromYear', (year - 1).toString());
                    updateParam('fromMonth', '');
                    updateParam('fromDay', '');
                    updateParam('toYear', (year - 1).toString());
                    updateParam('toMonth', '');
                    updateParam('toDay', '');
                    break;

                case 'all-time':
                    // Set fromYear to a very old year and toYear to a future year to show all expenses
                    updateParam('fromYear', '2000');
                    updateParam('fromMonth', '');
                    updateParam('fromDay', '');
                    updateParam('toYear', (year + 10).toString());
                    updateParam('toMonth', '');
                    updateParam('toDay', '');
                    break;
            }
        },
        [updateParam]
    );

    const clearFilters = useCallback(() => {
        updateParam('fromYear', currentYear.toString());
        updateParam('fromMonth', '');
        updateParam('fromDay', '');
        updateParam('toYear', '');
        updateParam('toMonth', '');
        updateParam('toDay', '');
        updateParam('categoryId', '');
        updateParam('type', '');
    }, [updateParam, currentYear]);

    return {
        filters,
        updateFilters,
        applyPreset,
        clearFilters,
    };
};

export default useExpenseFilters;
